import api from '../api';

/**
 * Terminal API service for managing terminal sessions
 */
export const TerminalAPI = {
  /**
   * Retry wrapper with exponential backoff
   * @param {Function} fn - Function to retry
   * @param {number} maxRetries - Maximum number of retries
   * @param {number} baseDelay - Base delay in milliseconds
   * @returns {Promise} - Returns function result or throws error after max retries
   */
  retryWithBackoff: async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        
        if (attempt === maxRetries) {
          console.error(`API call failed after ${maxRetries + 1} attempts:`, error);
          throw new Error(`Request failed after ${maxRetries + 1} attempts: ${error.message}`);
        }
        
        // Exponential backoff with jitter
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
        console.warn(`API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${Math.round(delay)}ms:`, error.message);
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  },

  /**
   * Check user's terminal permissions for a cluster
   * @param {string} clusterUniqueId - The cluster unique ID
   * @returns {Promise} - Returns permission status
   */
  checkPermissions: async (clusterUniqueId) => {
    return TerminalAPI.retryWithBackoff(async () => {
      const response = await api.get(`/api/cluster/terminal/${clusterUniqueId}/permissions/`);
      return response.data;
    });
  },

  /**
   * Create a new terminal session
   * @param {string} clusterUniqueId - The cluster unique ID
   * @param {object} sessionData - Session data (namespace, pod, container)
   * @returns {Promise} - Returns session information
   */
  createSession: async (clusterUniqueId, sessionData) => {
    return TerminalAPI.retryWithBackoff(async () => {
      const response = await api.post(`/api/cluster/terminal/${clusterUniqueId}/sessions/`, sessionData);
      return response.data;
    });
  },

  /**
   * Close a terminal session
   * @param {string} clusterUniqueId - The cluster unique ID
   * @param {string} sessionId - The session ID to close
   * @returns {Promise} - Returns success status
   */
  closeSession: async (clusterUniqueId, sessionId) => {
    return TerminalAPI.retryWithBackoff(async () => {
      const response = await api.delete(`/api/cluster/terminal/${clusterUniqueId}/sessions/${sessionId}/`);
      return response.data;
    });
  },

  /**
   * Get terminal sessions for a cluster
   * @param {string} clusterUniqueId - The cluster unique ID
   * @returns {Promise} - Returns list of sessions
   */
  getSessions: async (clusterUniqueId) => {
    return TerminalAPI.retryWithBackoff(async () => {
      const response = await api.get(`/api/cluster/terminal/${clusterUniqueId}/sessions/`);
      return response.data;
    });
  },

  /**
   * Get terminal audit logs
   * @param {string} clusterUniqueId - The cluster unique ID
   * @param {string} sessionId - Optional session ID for specific logs
   * @returns {Promise} - Returns audit logs
   */
  getAuditLogs: async (clusterUniqueId, sessionId = null) => {
    return TerminalAPI.retryWithBackoff(async () => {
      const url = sessionId 
        ? `/api/cluster/terminal/${clusterUniqueId}/audit-logs/${sessionId}/`
        : `/api/cluster/terminal/${clusterUniqueId}/audit-logs/`;
      const response = await api.get(url);
      return response.data;
    });
  }
};

export default TerminalAPI;

